import { useEffect, useMemo, useState } from 'react'
import { perfilParaEstado } from '../../../dominio/caminhaoTanque/perfil'
import { NOMES_USO, PONTOS_BAIXOS, PREDIOS_BEIRA_RODOVIA, type PredioBloco } from '../../../dominio/cenario/predios'
import {
  DESCRICAO_CLASSE,
  alcanceMaximoM,
  classeEstabilidade,
  concentracaoMaximaNosPontos,
  contornoNoSolo,
  sigmas,
  vazaoPorFuroKgS,
  type Insolacao,
  type Nebulosidade,
  type Pluma,
  type Terreno,
} from '../../../dominio/dispersao'
import {
  acaoProtetoraGre,
  comportaComoGasDenso,
  limiteZonaQuente,
  limitesDoProduto,
  motivoSemPluma,
  type IdLimite,
} from '../../../dominio/dispersao/produto'
import { produtoPorId, type IdProduto } from '../../../dominio/produtos'
import { formatarDistancia, mgM3ParaPpm } from '../../../dominio/unidades'
import { pontoCardeal } from '../../../dominio/vento'
import { CaminhaoTanque3D } from '../../../visualizacao3d/caminhaoTanque/CaminhaoTanque3D'
import type { VolumeEnquadramento } from '../../../visualizacao3d/camera/comandosCamera'
import { Biruta3D } from '../../../visualizacao3d/cenario/Biruta3D'
import { Pista3D } from '../../../visualizacao3d/cenario/Pista3D'
import { PontosBaixos3D } from '../../../visualizacao3d/cenario/PontosBaixos3D'
import { PredioBloco3D } from '../../../visualizacao3d/cenario/PredioBloco3D'
import { Terreno3D } from '../../../visualizacao3d/cenario/Terreno3D'
import { AreaAcaoProtetora3D, ContornosPluma3D, type ContornoDesenhavel } from '../../../visualizacao3d/zonas/ContornosPluma3D'
import { ControlesTimeline } from '../../ControlesTimeline'
import { IconeCamadas, IconeModuloPluma, IconeReproduzir, IconeResultados, IconeVento } from '../../icones'
import type { ConteudoInspetor } from '../../modulos/tipos'
import { useTimeline } from '../../useTimeline'
import { CascaOficina, type ContextoCasca } from '../../workspace/CascaOficina'
import { BarraTimeline } from '../comum/BarraTimeline'
import { LegendaZonas } from '../comum/LegendaZonas'
import { RosaDosVentos } from '../comum/RosaDosVentos'
import { SeletorProduto } from '../comum/SeletorProduto'
import { ValorCitado } from '../comum/ValorCitado'

const CORES_LIMITE: Record<IdLimite | 'zq', { cor: string; opacidade: number }> = {
  zq: { cor: '#ef4444', opacidade: 0.3 },
  aegl3: { cor: '#b91c1c', opacidade: 0.22 },
  idlh: { cor: '#ef4444', opacidade: 0.26 },
  aegl2: { cor: '#f97316', opacidade: 0.2 },
  aegl1: { cor: '#facc15', opacidade: 0.12 },
  lii: { cor: '#d946ef', opacidade: 0.25 },
  meioLii: { cor: '#f0abfc', opacidade: 0.14 },
}
const COR_GRE = '#60a5fa'
const ALCANCE_MINIMO_ROTULO_M = 150
const POSICAO_CAMINHAO: [number, number, number] = [1.75, 0, 0]

const fmt = (valor: number, casas = 1) => valor.toLocaleString('pt-BR', { maximumFractionDigits: casas, minimumFractionDigits: casas })

/** Notação legível de concentração: 3 algarismos significativos. */
const fmtConc = (valor: number) => (valor >= 100 ? Math.round(valor).toLocaleString('pt-BR') : valor.toLocaleString('pt-BR', { maximumSignificantDigits: 3 }))

const aegl2Alcance = (contornos: ReadonlyArray<{ limite: { id: IdLimite }; alcanceM: number | null }>) =>
  contornos.find((c) => c.limite.id === 'aegl2')?.alcanceM ?? null

const cantos = (p: PredioBloco): Array<readonly [number, number]> => {
  const a = p.larguraM / 2
  const b = p.profundidadeM / 2
  return [[p.x, p.z], [p.x - a, p.z - b], [p.x + a, p.z - b], [p.x - a, p.z + b], [p.x + a, p.z + b]]
}

/**
 * Oficina de vazamento tóxico: pluma gaussiana com estabilidade de Pasquill
 * e σ de Briggs, contornos AEGL/IDLH/LII no chão, prédios dentro de cada
 * contorno e, ao lado, a distância de proteção do GRE.
 */
export function OficinaPluma({ contexto }: { contexto: ContextoCasca }) {
  const [idProduto, setIdProduto] = useState<IdProduto>('amonia')
  const [modoVazao, setModoVazao] = useState<'furo' | 'informada'>('furo')
  const [diametroFuroMm, setDiametroFuroMm] = useState(25)
  const [vazaoInformada, setVazaoInformada] = useState(2)
  const [grandeVazamento, setGrandeVazamento] = useState(true)
  const [direcaoVento, setDirecaoVento] = useState(90)
  const [ventoMS, setVentoMS] = useState(3)
  const [periodo, setPeriodo] = useState<'dia' | 'noite'>('dia')
  const [insolacao, setInsolacao] = useState<Insolacao>('moderada')
  const [nebulosidade, setNebulosidade] = useState<Nebulosidade>('poucaNuvem')
  const [terreno, setTerreno] = useState<Terreno>('aberto')
  const [mostrarGre, setMostrarGre] = useState(true)
  const [idPredio, setIdPredio] = useState<string | null>(null)

  const produto = produtoPorId(idProduto)
  const perfil = useMemo(() => perfilParaEstado(produto.estadoTransporte), [produto.estadoTransporte])
  const podeUsarFuro = produto.densidadeLiquidoKgM3 != null && produto.pressaoVaporKPa != null
  const modo = podeUsarFuro ? modoVazao : 'informada'
  const vazaoKgS =
    modo === 'furo'
      ? vazaoPorFuroKgS({ diametroFuroMm, densidadeLiquidoKgM3: produto.densidadeLiquidoKgM3!.valor, pressaoVaporKPa: produto.pressaoVaporKPa!.valor })
      : vazaoInformada

  const classe = classeEstabilidade(periodo === 'dia' ? { ventoMS, periodo, insolacao } : { ventoMS, periodo, nebulosidade })
  const pluma: Pluma = useMemo(() => ({ vazaoKgS, ventoMS, classe, terreno, alturaFonteM: 0 }), [vazaoKgS, ventoMS, classe, terreno])

  const limites = useMemo(() => limitesDoProduto(produto), [produto])
  const zq = limiteZonaQuente(produto)
  const contornos = useMemo(() => {
    const lista = limites.map((limite) => {
      const borda = contornoNoSolo(pluma, limite.mgM3 * 1e-6)
      const alcance = borda.length ? borda[borda.length - 1]![0] : null
      return { limite, borda, alcanceM: alcance, larguraMaxM: borda.reduce((m, [, w]) => Math.max(m, 2 * w), 0) }
    })
    return lista
  }, [limites, pluma])
  const contornoZq = useMemo(() => {
    if (!zq || limites.some((l) => Math.abs(l.mgM3 - zq.mgM3) < 1e-9)) return null
    return contornoNoSolo(pluma, zq.mgM3 * 1e-6)
  }, [zq, limites, pluma])

  const menorLimite = limites[limites.length - 1]
  const alcanceTotalM = (menorLimite && alcanceMaximoM(pluma, menorLimite.mgM3 * 1e-6)) ?? 500
  const tempoChegadaS = alcanceTotalM / ventoMS
  const timeline = useTimeline(12)
  // a oficina abre com a pluma já estabelecida; a reprodução mostra o avanço
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => timeline.irPara(1), [])
  const frenteM = timeline.tempo >= 0.999 ? Infinity : timeline.tempo * tempoChegadaS * ventoMS

  const gre = acaoProtetoraGre(produto, { noite: periodo === 'noite', ventoMS, grandeVazamento })
  const denso = comportaComoGasDenso(produto)
  const aegl2 = contornos.find((c) => c.limite.id === 'aegl2')
  const massaMolar = produto.massaMolarGMol?.valor ?? null

  const predios = useMemo(
    () =>
      PREDIOS_BEIRA_RODOVIA.map((predio) => {
        const { kgM3, xM } = concentracaoMaximaNosPontos(pluma, direcaoVento, cantos(predio))
        const mgM3 = kgM3 * 1e6
        const acima = limites.filter((l) => mgM3 >= l.mgM3)
        return { predio, mgM3, chegadaMin: xM > 0 && Number.isFinite(xM) ? xM / ventoMS / 60 : null, limiteMaisAlto: acima[0] ?? null }
      }).sort((a, b) => b.mgM3 - a.mgM3),
    [pluma, direcaoVento, limites, ventoMS],
  )

  const desenhaveis: ContornoDesenhavel[] = [
    ...[...contornos].reverse().map((c) => ({
      id: c.limite.id,
      borda: c.borda,
      cor: CORES_LIMITE[c.limite.id].cor,
      opacidade: CORES_LIMITE[c.limite.id].opacidade,
      // contornos curtos ficam só na legenda: perto da fonte os rótulos se atropelam
      rotulo: (c.alcanceM ?? 0) < ALCANCE_MINIMO_ROTULO_M ? undefined : zq && Math.abs(c.limite.mgM3 - zq.mgM3) < 1e-9 ? `ZQ · ${c.limite.rotulo}` : c.limite.rotulo,
      tracejado: c.limite.id === 'meioLii',
    })),
    ...(contornoZq ? [{ id: 'zq', borda: contornoZq, cor: CORES_LIMITE.zq.cor, opacidade: CORES_LIMITE.zq.opacidade, rotulo: `ZQ · ${zq!.rotulo}` }] : []),
  ]

  const alcanceEnquadrado = Math.min(8000, Math.max(300, aegl2Alcance(contornos) ?? alcanceTotalM))
  const tamanho = Math.ceil((alcanceEnquadrado * 2.2) / 100) * 100
  const volume = useMemo<VolumeEnquadramento>(
    () => ({
      comprimento: tamanho,
      largura: tamanho,
      altura: 30,
      centro: [POSICAO_CAMINHAO[0], POSICAO_CAMINHAO[2]],
      alturaAlvoM: POSICAO_CAMINHAO[1] + 1.5,
    }),
    [tamanho],
  )
  const { executar } = contexto.camera
  useEffect(() => executar('restaurar'), [executar])

  const selecionado = predios.find((p) => p.predio.id === idPredio) ?? null
  const textoConc = (mgM3: number) =>
    massaMolar ? `${fmtConc(mgM3)} mg/m³ (${fmtConc(mgM3ParaPpm(mgM3, massaMolar))} ppm)` : `${fmtConc(mgM3)} mg/m³`
  const inspetor: ConteudoInspetor | null = selecionado && {
    titulo: selecionado.predio.nome,
    subtitulo: NOMES_USO[selecionado.predio.uso],
    propriedades: [
      { rotulo: 'Concentração máxima no solo', valor: selecionado.mgM3 > 1e-6 ? textoConc(selecionado.mgM3) : 'fora da pluma' },
      { rotulo: 'Limiar atingido', valor: selecionado.limiteMaisAlto ? `${selecionado.limiteMaisAlto.rotulo} — ${selecionado.limiteMaisAlto.significado}` : 'nenhum' },
      ...(selecionado.chegadaMin != null && selecionado.mgM3 > 1e-6
        ? [{ rotulo: 'Chegada da pluma', valor: `≈ ${fmt(selecionado.chegadaMin)} min após o início do vazamento` }]
        : []),
      ...(selecionado.limiteMaisAlto
        ? [{
            rotulo: 'Evacuar ou abrigar?',
            valor:
              'Para discutir: evacuar exige tempo antes da chegada e rota fora da pluma; abrigar no local (portas e janelas fechadas, ventilação desligada) costuma ser melhor quando a pluma chega antes ou passa rápido (GRE, "Ações de proteção").',
          }]
        : []),
    ],
    aoFechar: () => setIdPredio(null),
  }

  const grupos = [
    {
      id: 'liberacao',
      rotulo: 'Liberação',
      icone: <IconeModuloPluma />,
      resumo: `${fmt(vazaoKgS, 2)} kg/s de ${produto.nome}`,
      conteudo: () => (
        <div className="grupo-controle">
          <SeletorProduto idSelecionado={idProduto} aoSelecionar={setIdProduto} motivoIndisponivel={motivoSemPluma} />
          <span className="titulo-controle">Taxa de emissão</span>
          <label className="toggle">
            <input type="radio" name="modo-vazao" checked={modo === 'furo'} disabled={!podeUsarFuro} onChange={() => setModoVazao('furo')} />
            estimar por furo na fase líquida
          </label>
          <label className="toggle">
            <input type="radio" name="modo-vazao" checked={modo === 'informada'} onChange={() => setModoVazao('informada')} />
            informar a vazão
          </label>
          {modo === 'furo' ? (
            <label className="deslizante">
              <span>Diâmetro do furo <strong>{diametroFuroMm} mm</strong> → <strong>{fmt(vazaoKgS, 2)} kg/s</strong></span>
              <input type="range" min={5} max={100} step={1} value={diametroFuroMm} onChange={(e) => setDiametroFuroMm(Number(e.target.value))} />
            </label>
          ) : (
            <label className="deslizante">
              <span>Vazão <strong>{fmt(vazaoInformada, 1)} kg/s</strong></span>
              <input type="range" min={0.1} max={30} step={0.1} value={vazaoInformada} onChange={(e) => setVazaoInformada(Number(e.target.value))} />
            </label>
          )}
          <p className="nota-controle">
            Furo: Q = Cd·A·√(2ρ·ΔP), Cd = 0,61, ΔP = pressão de vapor − atmosférica. Considera que todo o líquido vira nuvem — conservador.
          </p>
          <label className="toggle">
            <input type="checkbox" checked={grandeVazamento} onChange={(e) => setGrandeVazamento(e.target.checked)} />
            grande vazamento (carreta) — para a comparação com o GRE
          </label>
        </div>
      ),
    },
    {
      id: 'meteorologia',
      rotulo: 'Meteorologia',
      icone: <IconeVento />,
      resumo: `de ${pontoCardeal(direcaoVento)}, ${ventoMS} m/s, classe ${classe}`,
      conteudo: () => (
        <div className="grupo-controle">
          <span className="titulo-controle">Vento — de onde vem</span>
          <RosaDosVentos direcaoGraus={direcaoVento} aoMudar={setDirecaoVento} />
          <p className="nota-controle">De {pontoCardeal(direcaoVento)} ({direcaoVento}°). Arraste o ponteiro; a seta tracejada mostra para onde a pluma vai.</p>
          <label className="deslizante">
            <span>Velocidade a 10 m <strong>{ventoMS} m/s</strong> ({fmt(ventoMS * 3.6, 0)} km/h)</span>
            <input type="range" min={1} max={10} step={0.5} value={ventoMS} onChange={(e) => setVentoMS(Number(e.target.value))} />
          </label>
          <div className="ribbon-pilulas-cad" role="group" aria-label="Período">
            {(['dia', 'noite'] as const).map((p) => (
              <button key={p} type="button" aria-pressed={periodo === p} className={`btn-acao-cad ${periodo === p ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`} onClick={() => setPeriodo(p)}>
                {p === 'dia' ? 'Dia' : 'Noite'}
              </button>
            ))}
          </div>
          {periodo === 'dia' ? (
            <label className="campo">
              Insolação
              <select value={insolacao} onChange={(e) => setInsolacao(e.target.value as Insolacao)}>
                <option value="forte">forte (sol alto, céu limpo)</option>
                <option value="moderada">moderada</option>
                <option value="fraca">fraca (sol baixo ou nuvens esparsas)</option>
                <option value="encoberto">céu encoberto</option>
              </select>
            </label>
          ) : (
            <label className="campo">
              Nebulosidade
              <select value={nebulosidade} onChange={(e) => setNebulosidade(e.target.value as Nebulosidade)}>
                <option value="poucaNuvem">céu limpo ou poucas nuvens (≤ 3/8)</option>
                <option value="nublado">nublado (≥ 4/8)</option>
              </select>
            </label>
          )}
          <label className="campo">
            Terreno
            <select value={terreno} onChange={(e) => setTerreno(e.target.value as Terreno)}>
              <option value="aberto">campo aberto (Briggs rural)</option>
              <option value="urbano">área urbana (Briggs urbano)</option>
            </select>
          </label>
          <p className="feedback-colocacao proxima" role="status">
            <strong>Classe {classe}</strong> {DESCRICAO_CLASSE[classe]}.
          </p>
        </div>
      ),
    },
    {
      id: 'camadas',
      rotulo: 'Camadas',
      icone: <IconeCamadas />,
      conteudo: () => (
        <div className="grupo-controle">
          <label className="toggle">
            <input type="checkbox" checked={mostrarGre} onChange={(e) => setMostrarGre(e.target.checked)} />
            área de ação protetora do GRE
          </label>
        </div>
      ),
    },
    {
      id: 'animacao',
      rotulo: 'Animação',
      icone: <IconeReproduzir />,
      conteudo: () => <ControlesTimeline timeline={timeline} fase={`Avanço da pluma: ${fmt(tempoChegadaS / 60)} min até ${formatarDistancia(alcanceTotalM)}`} />,
    },
  ]

  return (
    <CascaOficina
      contexto={contexto}
      oficina={{
        volume,
        cameraLivre: true,
        distanciaMaxima: 12000,
        corFundo: '#26333f',
        neblinaM: [4000, 11000],
        idSelecionado: idPredio ? `predio-${idPredio}` : null,
        nomeSelecionado: selecionado?.predio.nome ?? null,
        aoClicarVazio: () => setIdPredio(null),
        ferramentas: {
          grupos,
          chips: [
            { id: 'q', rotulo: 'Q', valor: `${fmt(vazaoKgS, 2)} kg/s` },
            { id: 'classe', rotulo: 'Classe', valor: classe },
            { id: 'vento', rotulo: 'Vento', valor: `${pontoCardeal(direcaoVento)} ${ventoMS} m/s` },
            ...(aegl2?.alcanceM ? [{ id: 'aegl2', rotulo: 'AEGL-2', valor: formatarDistancia(aegl2.alcanceM), tom: 'alerta' as const }] : []),
            ...(denso ? [{ id: 'denso', rotulo: 'Gás denso', valor: 'modelo subestima', tom: 'perigo' as const }] : []),
          ],
          inspetor,
        },
        operacao: (
          <div className="ficha-produto">
            {denso && (
              <p className="aviso-modelo" role="alert">
                <strong>Gás denso.</strong> {produto.nome} sai do tanque como nuvem mais pesada que o ar: escoa junto ao solo, entra
                em bueiros, galerias e baixadas e vai por onde o terreno manda. A pluma gaussiana trata o gás como neutro e
                subestima esse espalhamento — os pontos baixos estão destacados na cena.
              </p>
            )}
            <section>
              <h3 className="titulo-controle">Pluma</h3>
              <dl className="leituras">
                <div><dt>Taxa de emissão</dt><dd>{fmt(vazaoKgS, 2)} kg/s</dd></div>
                <div><dt>Classe de estabilidade</dt><dd><ValorCitado fonte="turner1970">{classe}</ValorCitado></dd></div>
                <div>
                  <dt>σy · σz a 1 km</dt>
                  <dd>
                    <ValorCitado fonte={terreno === 'urbano' ? 'epaIsc3' : 'briggs1973'}>
                      {fmt(sigmas(classe, 1000, terreno).sigmaYM, 0)} m · {fmt(sigmas(classe, 1000, terreno).sigmaZM, 0)} m
                    </ValorCitado>
                  </dd>
                </div>
                <div><dt>Tempo até {formatarDistancia(alcanceTotalM)}</dt><dd>{fmt(tempoChegadaS / 60)} min</dd></div>
              </dl>
            </section>
            <section>
              <h3 className="titulo-controle">Contornos no solo</h3>
              <table className="tabela-resultado">
                <thead><tr><th>Limiar</th><th className="numero">Alcance</th><th className="numero">Largura</th></tr></thead>
                <tbody>
                  {contornos.map(({ limite, alcanceM, larguraMaxM }) => (
                    <tr key={limite.id}>
                      <td>
                        <ValorCitado fonte={limite.fonte}>
                          <strong style={{ color: CORES_LIMITE[limite.id].cor }}>{limite.rotulo}</strong> {limite.valorOriginal}
                        </ValorCitado>
                        <br />
                        <small>{limite.significado}</small>
                      </td>
                      <td className="numero">{alcanceM ? formatarDistancia(alcanceM) : '—'}</td>
                      <td className="numero">{larguraMaxM ? formatarDistancia(larguraMaxM) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            {gre && (
              <section>
                <h3 className="titulo-controle">Comparação com o GRE</h3>
                <dl className="leituras">
                  <div className="leituras__largo"><dt>Situação</dt><dd>{gre.descricao}</dd></div>
                  <div><dt>Isolar (todas as direções)</dt><dd><ValorCitado fonte={gre.fonte}>{formatarDistancia(gre.isolarM)}</ValorCitado></dd></div>
                  <div><dt>Proteger a favor do vento</dt><dd><ValorCitado fonte={gre.fonte}>{gre.protegerM ? formatarDistancia(gre.protegerM) : '—'}</ValorCitado></dd></div>
                  <div><dt>Pluma calculada (AEGL-2)</dt><dd>{aegl2?.alcanceM ? formatarDistancia(aegl2.alcanceM) : '—'}</dd></div>
                </dl>
                <p className="nota-controle">
                  O GRE resume milhares de cenários num número só; o cálculo responde a este vazamento, com este tempo. Na
                  ocorrência, comece pelo GRE e refine com ferramenta oficial (ALOHA/CAMEO) e com monitoramento.
                </p>
              </section>
            )}
            <p className="aviso-modelo">
              Pluma gaussiana: vento e estabilidade constantes, terreno plano, sem prédios. As correlações de Briggs valem de
              100 m a 10 km; perto da fonte são extrapolação.
            </p>
          </div>
        ),
        checklist: (
          <div className="grupo-controle">
            <span className="titulo-controle">Prédios pela concentração</span>
            <table className="tabela-resultado">
              <thead><tr><th>Prédio</th><th className="numero">mg/m³</th><th>Limiar</th><th className="numero">Chega</th></tr></thead>
              <tbody>
                {predios.map(({ predio, mgM3, limiteMaisAlto, chegadaMin }) => (
                  <tr key={predio.id}>
                    <td><button type="button" className="ligacao" onClick={() => setIdPredio(predio.id)}>{predio.nome}</button></td>
                    <td className="numero">{mgM3 > 1e-6 ? fmtConc(mgM3) : '—'}</td>
                    <td>{limiteMaisAlto ? <strong style={{ color: CORES_LIMITE[limiteMaisAlto.id].cor }}>{limiteMaisAlto.rotulo}</strong> : '—'}</td>
                    <td className="numero">{limiteMaisAlto && chegadaMin != null ? `${fmt(chegadaMin)} min` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ),
        itensEstado: [
          { id: 'vento', rotulo: 'Vento', valor: `de ${pontoCardeal(direcaoVento)}, ${ventoMS} m/s · classe ${classe}`, icone: <IconeVento /> },
          {
            id: 'pluma',
            rotulo: 'Pluma',
            valor: timeline.tempo >= 0.999 ? 'estabelecida' : `${fmt(timeline.tempo * tempoChegadaS / 60)} min`,
            icone: <IconeResultados />,
          },
        ],
        sobreposicao: (
          <>
            <LegendaZonas
              titulo={`Pluma — ${produto.nome}`}
              itens={[
                ...contornos.filter((c) => c.alcanceM).map((c) => ({
                  id: c.limite.id,
                  cor: CORES_LIMITE[c.limite.id].cor,
                  rotulo: zq && Math.abs(c.limite.mgM3 - zq.mgM3) < 1e-9 ? `ZQ · ${c.limite.rotulo}` : c.limite.rotulo,
                  valor: formatarDistancia(c.alcanceM!),
                  tracejado: c.limite.id === 'meioLii',
                })),
                ...(contornoZq?.length ? [{ id: 'zq', cor: CORES_LIMITE.zq.cor, rotulo: `ZQ · ${zq!.rotulo}`, valor: formatarDistancia(contornoZq[contornoZq.length - 1]![0]) }] : []),
                ...(mostrarGre && gre ? [{ id: 'gre', cor: COR_GRE, rotulo: 'GRE: proteger', valor: formatarDistancia(gre.protegerM), tracejado: true }] : []),
              ]}
            />
            <BarraTimeline timeline={timeline} duracaoTotal={tempoChegadaS / 60} unidade="min" rotulo="avanço da pluma" />
          </>
        ),
        cena: (
          <>
            <Terreno3D ladoM={20000} />
            <Pista3D comprimentoM={8000} />
            <PontosBaixos3D pontos={PONTOS_BAIXOS} destacar={denso} />
            <Biruta3D posicao={[-14, 20]} direcaoVentoGraus={direcaoVento} velocidadeMS={ventoMS} />
            <group position={POSICAO_CAMINHAO}>
              <CaminhaoTanque3D perfil={perfil} numeroRisco={produto.numeroRisco.valor} onu={produto.onu} classes={[produto.classe.valor, ...produto.riscosSubsidiarios]} />
            </group>
            <ContornosPluma3D contornos={desenhaveis} direcaoVentoGraus={direcaoVento} frenteM={frenteM}>
              {mostrarGre && gre && <AreaAcaoProtetora3D isolarM={gre.isolarM} protegerM={gre.protegerM} cor={COR_GRE} />}
            </ContornosPluma3D>
            {predios.map(({ predio, limiteMaisAlto }) => (
              <PredioBloco3D
                key={predio.id}
                predio={predio}
                destaque={limiteMaisAlto ? CORES_LIMITE[limiteMaisAlto.id].cor : null}
                selecionado={predio.id === idPredio}
                mostrarNome={predio.id === idPredio || predio.uso === 'escola' || predio.uso === 'hospital'}
                aoSelecionar={setIdPredio}
              />
            ))}
          </>
        ),
      }}
    />
  )
}
